import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TrashIcon } from '../appearence/icons/trash_icon'
import { IconWrapper } from '../appearence/icons/icon_wrapper'
import { popupOpenService } from '../services/popupOpenService'
import { Tooltip } from './Tooltip'

type UsageRow = { count: number }

type ItemDeleteService = {
  getWhereItemIsUsed: (
    itemId: string,
    callback: (response: UsageRow[]) => void,
  ) => void
  getTranslationCode: () => string
  checkIsDeletedItem: (itemId: string) => boolean
}

type DeleteItemIconProps = {
  itemId: string
  itemDeleteService: ItemDeleteService
  popupKey: string
}

const usageCountCache = new Map<string, number>()

const cacheKey = (service: ItemDeleteService, itemId: string) =>
  `${service.getTranslationCode()}:${itemId}`

const sumUsage = (response: UsageRow[]) =>
  response.reduce((total, row) => total + (row.count || 0), 0)

const DeleteItemIcon = ({
  itemId,
  itemDeleteService,
  popupKey,
}: DeleteItemIconProps) => {
  const { i18n } = useTranslation()
  const key = cacheKey(itemDeleteService, itemId)
  const [count, setCount] = useState(() => usageCountCache.get(key) ?? 0)
  const [loaded, setLoaded] = useState(() => usageCountCache.has(key))
  const inFlight = useRef(false)

  const applyCount = (countValue: number) => {
    usageCountCache.set(key, countValue)
    setCount(countValue)
    setLoaded(true)
  }

  const loadUsage = (onDone?: (countValue: number) => void) => {
    if (itemDeleteService.checkIsDeletedItem(itemId) === true) {
      applyCount(0)
      onDone?.(0)
      return
    }
    if (usageCountCache.has(key)) {
      const cached = usageCountCache.get(key) ?? 0
      setCount(cached)
      setLoaded(true)
      onDone?.(cached)
      return
    }
    if (inFlight.current) {
      return
    }
    inFlight.current = true
    itemDeleteService.getWhereItemIsUsed(itemId, (response) => {
      inFlight.current = false
      const countValue = sumUsage(response || [])
      applyCount(countValue)
      onDone?.(countValue)
    })
  }

  const isDisabled = loaded && count > 0

  const openDeleteFormPopup = () => {
    if (loaded) {
      if (!isDisabled) {
        popupOpenService.callToOpen(popupKey, itemId)
      }
      return
    }
    loadUsage((countValue) => {
      if (countValue === 0) {
        popupOpenService.callToOpen(popupKey, itemId)
      }
    })
  }

  return (
    <Tooltip
      content={i18n.t(itemDeleteService.getTranslationCode())}
      enabled={isDisabled}
      placement="top-end"
    >
      <div
        key={`${itemId}deleteIcon`}
        onMouseEnter={() => {
          if (!loaded) {
            loadUsage()
          }
        }}
        onFocus={() => {
          if (!loaded) {
            loadUsage()
          }
        }}
        onClick={openDeleteFormPopup}
      >
        <IconWrapper>
          <TrashIcon />
        </IconWrapper>
      </div>
    </Tooltip>
  )
}

export default DeleteItemIcon
